import { isNull } from 'lodash'

import ApiHandler from '@/api/base'

class DataSourcesApiHandler extends ApiHandler {
  async getDataSources() {
    const { data } = await this.http.get('/datasources')
    return data
  }

  async deleteDataSource({ dataSourceId = null } = {}) {
    const { data } = await this.http.delete(`/datasources/${dataSourceId}`)
    return data
  }

  async unlinkQuery({ dataSourceId = null, dataSourceQueryId = null } = {}) {
    const { data } = await this.http.delete(
      `/datasources/${dataSourceId}/query/${dataSourceQueryId}`
    )
    return data
  }

  /**
   * Creates a query for a datasource.
   *
   * @param {Object} options - Query creation options.
   * @param {string|null} options.dataSourceId - The ID of the datasource to add the query to.
   * @param {string|null} options.elementId - The element ID associated with the query.
   * @param {string|null} options.displayName - The display name for the query.
   * @param {Object|null} options.params - Query parameters.
   * @param {string} [options.params.datasetId] - The dataset ID used in the query.
   * @param {string} [options.params.query] - The actual query string.
   * @returns {Promise<Object>} The created query object.
   * @throws {Error} If the HTTP request fails.
   *
   * @example
   * // Request body:
   * // {
   * //   elementId: '123',
   * //   displayName: 'Sales Data Query',
   * //   params: {
   * //     datasetId: 'dataset_1',
   * //     query: 'SELECT * FROM sales'
   * //   }
   * // }
   *
   * // Success Response:
   * // {
   * //   dataSourceId: 'ds_1',
   * //   dataSourceQueryId: 'q_1',
   * //   dataSourceTypeId: 'sql',
   * //   dataSourceQueryName: 'Sales Data Query',
   * //   isDataSourceOwner: true,
   * //   params: {
   * //     datasetId: 'dataset_1',
   * //     query: 'SELECT * FROM sales'
   * //   }
   * // }
   *
   * // 400 Error Response:
   * // {
   * //   errorCode: 'INVALID_QUERY_PARAMS'
   * // }
   */
  async createQuery({
    dataSourceId = null,
    elementId = null,
    displayName = null,
    params = {}
  } = {}) {
    const { data } = await this.http.post(`/datasources/${dataSourceId}/query`, {
      elementId,
      displayName,
      params
    })
    return data
  }

  /**
   * Update an existing query for a specific data source.
   *
   * @param {Object} options - Update options.
   * @param {string|null} options.dataSourceId - The ID of the data source.
   * @param {string|null} options.dataSourceQueryId - The ID of the query to update.
   * @param {string|null} options.displayName - The new display name for the query.
   * @param {Object|null} options.params - Updated query parameters.
   * @param {string} [options.params.datasetId] - The dataset ID used in the query.
   * @param {string} [options.params.query] - The actual query string.
   * @returns {Promise<Object>} The updated query object.
   * @throws {Error} If the HTTP request fails.
   *
   * @example
   * // Request body:
   * // {
   * //   displayName: 'Updated Query Name',
   * //   params: {
   * //     datasetId: 'dataset_1',
   * //     query: 'SELECT * FROM updated_table'
   * //   }
   * // }
   *
   * // Success Response (200):
   * // {
   * //   dataSourceId: 'ds_1',
   * //   dataSourceQueryId: 'q_1',
   * //   dataSourceTypeId: 'sql',
   * //   dataSourceQueryName: 'Updated Query Name',
   * //   isDataSourceOwner: true,
   * //   params: {
   * //     datasetId: 'dataset_1',
   * //     query: 'SELECT * FROM updated_table'
   * //   }
   * // }
   *
   * // 400 Error Response:
   * // {
   * //   errorCode: 'INVALID_QUERY_PARAMS'
   * // }
   */
  async updateQuery({
    dataSourceId = null,
    dataSourceQueryId = null,
    displayName = null,
    params = {}
  } = {}) {
    const { data } = await this.http.put(
      `/datasources/${dataSourceId}/query/${dataSourceQueryId}`,
      {
        displayName,
        params
      }
    )
    return data
  }

  /**
   * Retrieves details for a specific data source query.
   *
   * @param {Object} [options={}] - The request options.
   * @param {string|null} [options.dataSourceId=null] - The ID of the data source.
   * @param {string|null} [options.dataSourceQueryId=null] - The ID of the query within the data source.
   * @returns {Promise<Object>} The data source query object.
   *
   * @example
   * // Successful response structure:
   * // {
   * //   dataSourceId: 'ds_1',
   * //   dataSourceQueryId: 'q_1',
   * //   dataSourceTypeId: 'powerbi', // or 'google_sheets'
   * //   dataSourceQueryName: 'Query Name',
   * //   isDataSourceOwner: true,
   * //   isValid: true,
   * //   errorMessage: null,
   * //   params: {
   * //     datasetId: 'dataset_1',
   * //     query: 'SELECT * FROM my_table'
   * //   }
   * // }
   */
  async getQueryById({ dataSourceId = null, dataSourceQueryId = null } = {}) {
    const { data } = await this.http.get(`/datasources/${dataSourceId}/query/${dataSourceQueryId}`)
    return data
  }

  /**
   * Retrieves the list of datasets for a given data source.
   *
   * @param {Object} [options={}] - The request options.
   * @param {string|null} [options.dataSourceId=null] - The ID of the data source.
   * @param {number} [options.limit=50] - Maximum number of datasets to return.
   * @param {number} [options.startAt=0] - The offset for pagination.
   * @param {string|null} [options.searchString=null] - A query string to filter the datasets by name.
   * @param {string|null} [options.requiredId=null] - An ID of a dataset that should always be included in the results if available.
   * @returns {Promise<Object>} The response object containing an array of datasets.
   *
   * @example
   * // Example response:
   * // {
   * //   datasets: [
   * //     {
   * //       id: "cfafbeb1-8037-4d0c-896e-a46fb27ff229",
   * //       name: "SalesMarketing"
   * //     }
   * //   ]
   * // }
   */
  async getDatasets({
    dataSourceId = null,
    limit = 50,
    startAt = 0,
    searchString = null,
    requiredId = null
  } = {}) {
    if (!isNull(searchString)) {
      limit = 1000
      startAt = 0
      requiredId = null
    }

    const { data } = await this.http.get(`/datasources/${dataSourceId}/params/datasets`, {
      params: {
        limit,
        startAt,
        searchString,
        requiredId
      }
    })
    return data
  }
}

export default DataSourcesApiHandler
